/**
 * Seeded PRNG. Wave composition, spawn jitter and particle scatter all draw
 * from a seeded stream so a run is reproducible — which is what makes the
 * balance simulator and the visual regression shots meaningful.
 */

/** sfc32. Fast, passes PractRand, 128 bits of state. */
export function sfc32(a: number, b: number, c: number, d: number): () => number {
  return function () {
    a |= 0; b |= 0; c |= 0; d |= 0;
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    c = (c + t) | 0;
    return (t >>> 0) / 4294967296;
  };
}

/** Expands a string seed into four 32-bit words. */
export function seedFrom(str: string): () => number {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  const mix = (h: number) => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
  return sfc32(mix(h1), mix(h2), mix(h3), mix(h4));
}

export const range = (r: () => number, lo: number, hi: number) => lo + r() * (hi - lo);
export const rangeInt = (r: () => number, lo: number, hi: number) => Math.floor(lo + r() * (hi - lo + 1));
export const pick = <T,>(r: () => number, arr: readonly T[]): T => arr[Math.floor(r() * arr.length)];

/** Unit vector at a uniformly random angle. Writes into `out` to avoid garbage. */
export function unitVec(r: () => number, out: { x: number; y: number }): void {
  const a = r() * Math.PI * 2;
  out.x = Math.cos(a);
  out.y = Math.sin(a);
}

/** Box-Muller, cached second sample. Used for particle velocity spread. */
let spare: number | null = null;
export function gaussian(r: () => number): number {
  if (spare !== null) { const s = spare; spare = null; return s; }
  let u = 0, v = 0, s = 0;
  do { u = r() * 2 - 1; v = r() * 2 - 1; s = u * u + v * v; } while (s >= 1 || s === 0);
  const m = Math.sqrt(-2 * Math.log(s) / s);
  spare = v * m;
  return u * m;
}
